import { PanelItem, PanelItemFieldTypeEnum, PointOfInterest } from '@/__generated__/graphql';
import { CustomTextField, DropDownField } from '@/components/shared/CustomField';
import { CustomDateField } from '@/components/shared/CustomField/FieldTemplates/CustomDateField';
import { usePoiStatusLabels } from '@/hooks/usePoiStatusLabels';
import { AllPointOfInterestTemplate } from '@/types';
import { TemplateFieldRenderer } from '../../shared/TemplateFieldRenderer';
import { UseGeneralFieldsHookType } from '../../hooks/useGeneralFields';
import { UseTemplateFieldsHookType } from '../../hooks/useTemplateFields';
import { GeneralFieldsType } from '../../types';
import { POI_INPUT_CONFIGS } from './panelEditability';
import { BlisterDataHookType, editPanelFeatureRegistry } from './editPanelFeatureRegistry';

type Props = {
  item: PanelItem;
  poi: Partial<PointOfInterest>;
  template: AllPointOfInterestTemplate[0] | undefined;
  generalFieldsData: UseGeneralFieldsHookType;
  templateDataHook: UseTemplateFieldsHookType;
  blisterDataHook: BlisterDataHookType;
};

const NOOP_INPUT_ADORNMENT_HANDLER_MAP = {
  MEASUREMENT_TOOL: () => {},
  RELOAD: () => {},
  '': () => {},
};

export const PanelItemEditor = ({
  item,
  poi,
  template,
  generalFieldsData,
  templateDataHook,
  blisterDataHook,
}: Props) => {
  const { getPoiStatusLabel, getStatusOptions } = usePoiStatusLabels();
  const label = item.label ?? item.key;
  const poiTypeForStatus = template?.key ?? poi.type;

  if (item.feature) {
    const FeatureComponent = editPanelFeatureRegistry[item.feature];
    return FeatureComponent ? (
      <FeatureComponent
        poi={poi}
        template={template}
        templateDataHook={templateDataHook}
        blisterDataHook={blisterDataHook}
      />
    ) : null;
  }

  if (item.fieldType === PanelItemFieldTypeEnum.TemplateField) {
    const templateField = (template?.fields ?? []).find((field) => field.key === item.key);
    if (!templateField || templateField.isReadOnly) return null;
    const value = templateDataHook.state[item.key] ?? '';
    return (
      <TemplateFieldRenderer
        templateField={templateField}
        isEditing
        value={value}
        onChange={(next) => templateDataHook.handleChange(item.key, next)}
        inputAdormantHandlerMap={NOOP_INPUT_ADORNMENT_HANDLER_MAP}
      />
    );
  }

  if (item.fieldType === PanelItemFieldTypeEnum.Blister) {
    return null;
  }

  const fieldKey = item.key as keyof GeneralFieldsType;
  const inputConfig = POI_INPUT_CONFIGS[fieldKey];
  if (!inputConfig) return null;

  const { state, updateValue } = generalFieldsData;

  if (inputConfig.kind === 'status') {
    const statusOptions = poiTypeForStatus ? getStatusOptions(poiTypeForStatus) : [];
    return (
      <DropDownField
        mode="Edit"
        id={item.key}
        title={label}
        options={statusOptions.map((opt) => opt.label)}
        value={
          state.status && poiTypeForStatus ? getPoiStatusLabel(state.status, poiTypeForStatus) : ''
        }
        onFieldValueChange={(selectedLabel) => {
          const selected = statusOptions.find((opt) => opt.label === selectedLabel);
          if (selected) updateValue('status', selected.value);
        }}
      />
    );
  }

  if (inputConfig.kind === 'enumSelect') {
    return (
      <DropDownField
        mode="Edit"
        id={item.key}
        title={label}
        options={[...(inputConfig.enumValues ?? [])]}
        value={(state[fieldKey] as string | undefined) ?? ''}
        onFieldValueChange={(value) =>
          updateValue(fieldKey, value as GeneralFieldsType[keyof GeneralFieldsType])
        }
      />
    );
  }

  if (inputConfig.kind === 'date') {
    return (
      <CustomDateField
        format="dd MMM yyyy"
        mode="Edit"
        id={item.key}
        title={label}
        value={state.dueDate}
        sx={{ mb: 0 }}
        onFieldValueChange={(value) => updateValue('dueDate', value)}
      />
    );
  }

  return (
    <CustomTextField
      mode="Edit"
      id={item.key}
      title={label}
      value={(state[fieldKey] as string | undefined) ?? ''}
      onFieldValueChange={(value) =>
        updateValue(fieldKey, value as GeneralFieldsType[keyof GeneralFieldsType])
      }
    />
  );
};
